/**
 * Iter-156B Phase 1 — Shared visibility picker for Notes, Documents
 * and Links.
 *
 * Single source of truth so every surface speaks the same language
 * about who-can-see-what. Lightweight: a labelled <select> + helper
 * sentence + per-option description that updates live as the user
 * cycles options. No Shadcn Popover overhead — this is a leaf
 * control that needs to render inside dialogs that are themselves
 * fully accessible.
 *
 * The control is intentionally low-chrome so it fits comfortably
 * inside both the document upload dialog and the eventual note
 * create dialog without dominating the form.
 *
 * Backend tokens (everyone / owner_admin / uploader_only) are
 * unchanged from Iter-134; this component only changes the strings
 * the user reads.
 */
import { Eye } from "lucide-react";
import {
    VISIBILITY_OPTIONS,
    VISIBILITY_HELPER,
    VISIBILITY_DESCRIPTIONS,
} from "../lib/visibility";

export default function VisibilityPicker({
    value = "everyone",
    onChange,
    disabled = false,
    testidPrefix = "visibility",
    showHelper = true,
    showDescription = true,
    label = "Visibility",
}) {
    const current = VISIBILITY_DESCRIPTIONS[value] || VISIBILITY_DESCRIPTIONS.everyone;
    return (
        <div
            data-testid={`${testidPrefix}-picker`}
            className="text-xs text-neutral-300 block"
        >
            <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                <Eye size={11} className="opacity-70" />
                {label}
            </span>
            <select
                data-testid={`${testidPrefix}-select`}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                disabled={disabled}
                className="w-full h-11 bg-secondary border border-white/10 rounded-md px-3 text-sm text-foreground disabled:opacity-50"
            >
                {VISIBILITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
            {showDescription ? (
                <p
                    data-testid={`${testidPrefix}-description`}
                    className="text-[11px] text-neutral-400 mt-1.5 leading-relaxed"
                >
                    {current}
                </p>
            ) : null}
            {showHelper ? (
                <p
                    data-testid={`${testidPrefix}-helper`}
                    className="text-[10px] text-neutral-500 mt-1 leading-relaxed"
                >
                    {VISIBILITY_HELPER}
                </p>
            ) : null}
        </div>
    );
}
